import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useGame } from '../contexts/GameContext'
import { useWebSocket } from '../contexts/WebSocketContext'
import { useTournaments } from '../contexts/TournamentContext'
import { apiUrl } from '../config/api'
import Game from './Game/Game'
import Profile from './Profile/Profile'
import PublicProfile from './PublicProfile/PublicProfile'
import Leaderboard from './Leaderboard/Leaderboard'
import Rules from './Rules/Rules'
import GamePreferences from './GamePreferences/GamePreferences'
import WhyRiftChess from './WhyRiftChess/WhyRiftChess'
import ConfirmModal from './Modal/ConfirmModal'
import Tournaments from './Tournaments/Tournaments'
import TournamentDetails from './TournamentDetails/TournamentDetails'
import UserDisplay from './UserDisplay/UserDisplay'
import Replay from './Replay/Replay'
import ReplayImporter from './ReplayImporter/ReplayImporter'
import ComingSoon from './ComingSoon/ComingSoon'
import Premium from './Premium/Premium'
import Friends from './Friends/Friends'
import Notifications from './Notifications/Notifications'
import QuestAccountModal from './Modal/QuestAccountModal'
import PlatformStats from './PlatformStats/PlatformStats'
import FairPlay from './FairPlay/FairPlay'
import AdminPage from '../pages/AdminPage'
import SpectateGamesList from '../pages/SpectateGamesList'
import LiveGamesPreview from './LiveGames/LiveGamesPreview'
import MatchmakingDashboard from './MatchmakingDashboard/MatchmakingDashboard'

const TIME_CONTROL_PRESETS = {
  classic: {
    label: '30+0',
    description: 'Classical time control - 30 minutes each',
    category: 'Classical',
    config: {
      initial_time: 1800, // 30 minutes in seconds
      increment: 0
    }
  },
  rapid: {
    label: '10+0',
    description: 'Rapid time control - 10 minutes each',
    category: 'Rapid',
    config: {
      initial_time: 600, // 10 minutes in seconds
      increment: 0
    }
  },
  blitz_5_2: {
    label: '5+2',
    description: 'Blitz with 2-second increment',
    category: 'Blitz',
    config: {
      initial_time: 300, // 5 minutes in seconds
      increment: 2
    }
  },
  blitz_3_2: {
    label: '3+2',
    description: 'Fast blitz with 2-second increment',
    category: 'Blitz',
    config: {
      initial_time: 180, // 3 minutes in seconds
      increment: 2
    }
  },
  bullet: {
    label: '1+0',
    description: 'Bullet chess - 1 minute each',
    category: 'Bullet',
    config: {
      initial_time: 60, // 1 minute in seconds
      increment: 0
    }
  }
}

export default function Dashboard() {
  const { user, profile: authProfile, signOut, isGuest, triggerRegistration, session } = useAuth()
  const {
    createGame,
    joinGame,
    availableGames,
    listAvailableGames,
    games,
    listGames,
    currentGame,
    error,
    getGame,
    setCurrentGame,
    getProfile,
    getMatchHistory,
    resignGame,
    deleteGame
  } = useGame()


  const { tournaments, listTournaments, joinTournament, leaveTournament } = useTournaments()
  const {
    addMessageHandler,
    removeMessageHandler,
    isConnected,
    joinGame: joinWebSocketGame,
    subscribe
  } = useWebSocket()
  const [view, setView] = useState('dashboard') // 'dashboard', 'game', 'profile', 'leaderboard', 'publicProfile', 'tournaments', 'tournamentDetails', 'friends', 'notifications', 'teams', 'admin'
  const [loading, setLoading] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState(null)
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const [timePreset, setTimePreset] = useState('rapid')
  const [gameRated, setGameRated] = useState(!isGuest)
  const [profileStats, setProfileStats] = useState(null)
  const [lastGame, setLastGame] = useState(null)
  const [showAbandonModal, setShowAbandonModal] = useState(false)
  const [gameToAbandon, setGameToAbandon] = useState(null)
  const [publicProfileUsername, setPublicProfileUsername] = useState(null)
  const [replayGameId, setReplayGameId] = useState(null)
  const [replayMoveNumber, setReplayMoveNumber] = useState(0)
  const [selectedTournamentId, setSelectedTournamentId] = useState(null)
  const [selectedTournamentTab, setSelectedTournamentTab] = useState('big')
  const [currentTime, setCurrentTime] = useState(new Date())
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0)
  const [showQuestModal, setShowQuestModal] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [showPrivateGameModal, setShowPrivateGameModal] = useState(false)
  const [privateGameUrl, setPrivateGameUrl] = useState('')
  const [privateGameId, setPrivateGameId] = useState('')
  const [sessionWins, setSessionWins] = useState({})

  // Listen for matchmaking game_started events
  useEffect(() => {
    if (!subscribe) return

    const handleMessage = (msg) => {
      // Logic remains the same, but now we know we only get 'game_started' messages
      if (msg.data?.game_id) {
          console.log('Match found! Joining game:', msg.data.game_id)
          handleNavigateToGame(msg.data.game_id)
      }
    }

    console.log('Dashboard subscribing to game_started events')
    return subscribe('game_started', handleMessage)
  }, [subscribe])

  const handleSignOut = async () => {
    await signOut()
    setView('dashboard')
    // Clear tournament state that might cause navigation issues
    setSelectedTournamentId(null)
    setSelectedTournamentTab('big')
    // Reset URL to root to avoid navigation issues after login
    window.history.pushState({}, '', '/')
  }

  const fetchUnreadNotificationCount = async () => {
    if (!user || isGuest) {
      setUnreadNotificationCount(0)
      return
    }

    try {
      const API_BASE = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE
      const authData = JSON.parse(localStorage.getItem('auth'))

      if (!authData?.session?.access_token) return

      const response = await fetch(`${API_BASE}/notifications/count`, {
        headers: {
          'Authorization': `Bearer ${authData.session.access_token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setUnreadNotificationCount(data.count || 0)
      }
    } catch (error) {
      console.error('Failed to fetch notification count:', error)
    }
  }

  const checkAdminStatus = async () => {
    if (!user || isGuest) {
      setIsAdmin(false)
      return
    }

    try {
      const API_BASE = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE
      const authData = JSON.parse(localStorage.getItem('auth'))

      if (!authData?.session?.access_token) return

      const response = await fetch(`${API_BASE}/admin/check`, {
        headers: {
          'Authorization': `Bearer ${authData.session.access_token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setIsAdmin(data.is_admin || false)
      }
    } catch (error) {
      setIsAdmin(false)
    }
  }

  const handleCreateGame = async (isPrivate = false) => {
    try {
      setLoading(true)

      // Check if user already has 3 or more waiting games
      const myWaitingGames = (games || []).filter(game =>
        (game.status === 'waiting' || game.status === 'pregame') &&
        (game.white_player_id === user?.id || game.black_player_id === user?.id)
      )

      if (myWaitingGames.length >= 3) {
        alert('You can only have 3 open games at a time. Please finish or abandon some games first.')
        return
      }

      const preset = TIME_CONTROL_PRESETS[timePreset] || TIME_CONTROL_PRESETS.rapid

      console.log('Creating game with time control:', preset.config)

      const game = await createGame({
        time_control: preset.config,
        rated: gameRated && !isGuest,
        private: isPrivate
      })

      // Immediately join WebSocket room for the created game so we can receive updates
      if (isConnected && game?.id) {
        joinWebSocketGame(game.id)
      }

      if (isPrivate) {
        // Show private game modal instead of switching to game view immediately
        const gameUrl = `https://www.riftchess.com/game/${game.id}`
        setPrivateGameUrl(gameUrl)
        setPrivateGameId(game.id)
        setShowPrivateGameModal(true)
        // Don't switch to game view yet - let the modal handle it
      } else {
        setView('game')
      }
    } catch (err) {
      console.error('Failed to create game:', err)
    } finally {
      setLoading(false)
    }
  }



  const handleJoinGame = async (gameId) => {
    try {
      setLoading(true)
      await joinGame(gameId)
      setView('game')
    } catch (err) {
      console.error('Failed to join game:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleResumeGame = async (gameId) => {
    try {
      setLoading(true)
      const game = await getGame(gameId)
      setCurrentGame(game)
      setView('game')
    } catch (err) {
      console.error('Failed to resume game:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleBackToDashboard = () => {
    setView('dashboard')
    // Clear the URL to prevent refresh from taking user back to game
    window.history.replaceState({}, '', '/')
  }

  const handleNavigateToGame = async (gameId) => {
    try {
      const game = await getGame(gameId)
      setCurrentGame(game)
      // Join WebSocket room for the new game
      if (isConnected) {
        joinWebSocketGame(gameId)
      }
      setView('game')
      window.history.pushState({}, '', `/game/${gameId}`)
    } catch (err) {
      console.error('Failed to navigate to game:', err)
    }
  }

  const handleViewPublicProfile = (username) => {
    setPublicProfileUsername(username)
    setView('publicProfile')
    // Update URL without reloading page
    window.history.pushState({}, '', `/user/${encodeURIComponent(username)}`)
  }

  const handleBackFromPublicProfile = () => {
    // Use browser history to go back
    window.history.back()
  }

  // Helper function to navigate to different views with URL updates
  const navigateToView = (newView, urlPath = '/') => {
    setView(newView)
    if (newView !== 'publicProfile') {
      setPublicProfileUsername(null)
    }
    if (urlPath !== window.location.pathname) {
      window.history.pushState({}, '', urlPath)
    }
  }

  const handleViewLeaderboard = () => {
    setView('leaderboard')
    window.history.pushState({}, '', '/leaderboard')
  }

  const handleViewProfile = () => {
    setView('profile')
    window.history.pushState({}, '', '/profile')
  }

  const handleViewTournaments = () => {
    setView('tournaments')
    window.history.pushState({}, '', '/tournaments')
  }

  const handleViewFriends = () => {
    setView('friends')
    window.history.pushState({}, '', '/friends')
  }

  const handleViewNotifications = () => {
    setView('notifications')
    window.history.pushState({}, '', '/notifications')
  }

  const handleViewDashboard = () => {
    setView('dashboard')
    window.history.pushState({}, '', '/')
  }

  const handleViewRules = () => {
    setView('rules')
    window.history.pushState({}, '', '/rules')
  }

  const handleViewFairPlay = () => {
    setView('fairplay')
    window.history.pushState({}, '', '/fairplay')
  }

  const handleViewWhyRiftChess = () => {
    setView('whyriftchess')
    window.history.pushState({}, '', '/why-riftchess')
  }

  const handleViewPlatformStats = () => {
    setView('platformStats')
    window.history.pushState({}, '', '/stats')
  }

  const handleViewAdmin = () => {
    setView('admin')
    window.history.pushState({}, '', '/admin')
  }

  const handleViewSpectate = () => {
    setView('spectate')
    window.history.pushState({}, '', '/spectate')
  }

  const handleViewReplay = (gameId, moveNumber = 0) => {
    setReplayGameId(gameId)
    setReplayMoveNumber(moveNumber)
    setView('gameReplay')
  }

  const handleBackFromReplay = () => {
    setReplayGameId(null)
    setReplayMoveNumber(0)
    setView('dashboard') // Go back to dashboard
  }

  const handleShowTournamentDetails = (tournamentId, fromTab = 'big') => {
    setSelectedTournamentId(tournamentId)
    setSelectedTournamentTab(fromTab)
    setView('tournamentDetails')
    window.history.pushState({}, '', `/tournaments/${tournamentId}`)
  }

  const handleBackFromTournamentDetails = () => {
    setSelectedTournamentId(null)
    setView('tournaments')
    window.history.pushState({}, '', '/tournaments')
  }

  const handleTournamentJoin = async (tournamentId) => {
    // If user is a guest, prompt them to create an account
    if (isGuest) {
      setShowQuestModal(true)
      return
    }

    try {
      setLoading(true)
      await joinTournament(tournamentId)
      // Refresh tournaments list to update registration status
      await listTournaments()
      // Show success toast (optional, but good UX)
      if (window.showToast) {
        window.showToast('Joined tournament successfully!', 'success')
      }
    } catch (error) {
      console.error('Failed to join tournament:', error)
      if (window.showToast) {
        window.showToast(error.message || 'Failed to join tournament', 'error')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleTournamentLeave = async (tournamentId) => {
    try {
      setLoading(true)
      await leaveTournament(tournamentId)
      // Refresh tournaments list to update registration status
      await listTournaments()
    } catch (error) {
      console.error('Failed to leave tournament:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateAccount = () => {
    setShowQuestModal(false)
    triggerRegistration()
  }

  const handleNavigateToAuth = (page) => {
    if (page === 'premium') {
      // Navigate to premium page
      setView('premium')
    } else {
      // Default to registration
      setShowQuestModal(false)
      triggerRegistration()
    }
  }

  const copyPrivateGameUrl = () => {
    navigator.clipboard.writeText(privateGameUrl).then(() => {
      // You could add a toast notification here
      console.log('Link copied to clipboard')
    }).catch(() => {
      console.error('Failed to copy link')
    })
  }

  const handleJoinPrivateGame = () => {
    setShowPrivateGameModal(false)
    setView('game')
  }

  const handleJoinGameFromLink = async (gameId) => {
    try {
      // Get game info first to check if it's private and if we're part of it
      const game = await getGame(gameId)

      // Check if we're already part of this game
      const isWhitePlayer = game.white_player_id === user?.id
      const isBlackPlayer = game.black_player_id === user?.id
      const isParticipant = isWhitePlayer || isBlackPlayer


      if (isParticipant) {
        // We're already part of the game, just resume it
        await handleResumeGame(gameId)
        if (isConnected) {
          joinWebSocketGame(gameId)
        }
      } else {
        // We're not part of the game, try to join it
        await joinGame(gameId)

        // Ensure WebSocket connection for real-time updates
        if (isConnected) {
          joinWebSocketGame(gameId)
        }

        setView('game')
      }
    } catch (error) {
      console.error('Failed to join or resume game:', error)
      // Could show an error message here
    }
  }

  const handleAbandonGame = (gameId) => {
    setGameToAbandon(gameId)
    setShowAbandonModal(true)
  }

  const confirmAbandonGame = async () => {
    if (!gameToAbandon) return

    try {
      setLoading(true)
      await deleteGame(gameToAbandon)
      await listAvailableGames()
    } catch (err) {
      console.error('Failed to abandon game:', err)
    } finally {
      setLoading(false)
      setGameToAbandon(null)
    }
  }

  const refreshAvailableGames = () => {
    listAvailableGames()
    listGames()
  }


  // Helper function to check if a game belongs to the current user
  const isMyGame = (game) => {
    return game.white_player_id === user?.id || game.black_player_id === user?.id
  }

  // Load user games on component mount and handle URL parameters
  useEffect(() => {
    if (!user) return // Don't fetch if no user
    listGames()
    listTournaments() // Load all tournaments for the featured section
    fetchUnreadNotificationCount() // Load notification count
    checkAdminStatus() // Check if user is admin


    // Check for direct game URL (e.g., /game/123 or /?game=123)
    const urlParams = new URLSearchParams(window.location.search)
    const gameIdFromUrl = urlParams.get('game')
    const pathMatch = window.location.pathname.match(/^\/game\/([a-f0-9-]+)$/i)
    const gameIdFromPath = pathMatch?.[1]

    // Check for user profile URL (e.g., /user/username)
    const userProfileMatch = window.location.pathname.match(/^\/user\/([^\/]+)$/i)
    const usernameFromPath = userProfileMatch?.[1]

    // Check for other view URLs
    const currentPath = window.location.pathname

    if (gameIdFromUrl || gameIdFromPath) {
      const gameId = gameIdFromUrl || gameIdFromPath
      // Try to join the game first (for friends with private links), then resume if already in it
      handleJoinGameFromLink(gameId)
    } else if (usernameFromPath) {
      // Navigate to public profile
      setPublicProfileUsername(decodeURIComponent(usernameFromPath))
      setView('publicProfile')
    } else if (currentPath === '/leaderboard') {
      setView('leaderboard')
    } else if (currentPath === '/profile') {
      setView('profile')
    } else if (currentPath === '/tournaments') {
      setView('tournaments')
    } else if (currentPath.startsWith('/tournaments/')) {
      const tournamentId = currentPath.replace('/tournaments/', '')
      setSelectedTournamentId(tournamentId)
      setView('tournamentDetails')
    } else if (currentPath === '/friends') {
      setView('friends')
    } else if (currentPath === '/notifications') {
      setView('notifications')
    } else if (currentPath === '/rules') {
      setView('rules')
    } else if (currentPath === '/fairplay') {
      setView('fairplay')
    } else if (currentPath === '/why-riftchess') {
      setView('whyriftchess')
    } else if (currentPath === '/stats') {
      setView('platformStats')
    } else if (currentPath === '/admin') {
      setView('admin')
    } else if (currentPath === '/spectate' || currentPath.startsWith('/spectate/')) {
      setView('spectate')
    } else if (currentPath === '/premium') {
      setView('premium')
    }
    // ... (rest of logic)
  }, [user?.id])

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = (event) => {
      const path = window.location.pathname

      // Parse the URL and set appropriate view
      if (path === '/' || path === '') {
        setView('dashboard')
        setPublicProfileUsername(null)
      } else {
        const userProfileMatch = path.match(/^\/user\/([^\/]+)$/i)
        const gameMatch = path.match(/^\/game\/([a-f0-9-]+)$/i)

        if (userProfileMatch) {
          const username = decodeURIComponent(userProfileMatch[1])
          setPublicProfileUsername(username)
          setView('publicProfile')
        } else if (gameMatch) {
          const gameId = gameMatch[1]
          handleJoinGameFromLink(gameId)
        } else if (path === '/leaderboard') {
          setView('leaderboard')
          setPublicProfileUsername(null)
        } else if (path === '/profile') {
          setView('profile')
          setPublicProfileUsername(null)
        } else if (path === '/tournaments') {
          setView('tournaments')
          setPublicProfileUsername(null)
          setSelectedTournamentId(null)
        } else if (path.startsWith('/tournaments/')) {
          const tournamentId = path.replace('/tournaments/', '')
          setSelectedTournamentId(tournamentId)
          setView('tournamentDetails')
          setPublicProfileUsername(null)
        } else if (path === '/friends') {
          setView('friends')
          setPublicProfileUsername(null)
        } else if (path === '/notifications') {
          setView('notifications')
          setPublicProfileUsername(null)
        } else if (path === '/rules') {
          setView('rules')
          setPublicProfileUsername(null)
        } else if (path === '/fairplay') {
          setView('fairplay')
          setPublicProfileUsername(null)
        } else if (path === '/why-riftchess') {
          setView('whyriftchess')
          setPublicProfileUsername(null)
        } else if (path === '/stats') {
          setView('platformStats')
          setPublicProfileUsername(null)
        } else if (path === '/admin') {
          setView('admin')
          setPublicProfileUsername(null)
        } else if (path === '/spectate' || path.startsWith('/spectate/')) {
          setView('spectate')
          setPublicProfileUsername(null)
        } else if (path === '/premium') {
          setView('premium')
          setPublicProfileUsername(null)
        } else {
          // Unknown route, go to dashboard
          setView('dashboard')
          setPublicProfileUsername(null)
        }
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  useEffect(() => {
    let isMounted = true
    const loadProfile = async () => {
      if (!user) {
        setProfileStats(null)
        setLastGame(null)
        return
      }
      try {
        // If authProfile is available, use it directly. Otherwise fetch.
        let profileData = authProfile
        if (!profileData && session?.access_token) {
           // Fallback if AuthContext hasn't loaded it yet
           profileData = await getProfile()
        }

        const matchHistory = await getMatchHistory(1) // Get just the last game
        
        if (isMounted) {
          setProfileStats(profileData)
          setLastGame(matchHistory && matchHistory.length > 0 ? matchHistory[0] : null)
        }
      } catch (err) {
        console.error('Failed to load profile:', err)
      }
    }
    loadProfile()
    return () => {
      isMounted = false
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, authProfile])

  // Handle WebSocket messages for game state updates
  // Ref to hold the debounce timeout for refreshing games
  const refreshTimeoutRef = useRef(null)

  useEffect(() => {
    if (!isConnected) return

    const handleGameStateUpdate = (message) => {
      const gameData = message.data

      // If this is our current game, update it immediately
      if (currentGame && gameData.id === currentGame.id) {
        setCurrentGame(gameData)

        // If the game status changed to 'pregame' or 'active' (opponent joined), automatically switch to game view
        if ((gameData.status === 'pregame' && currentGame.status === 'waiting') ||
            (gameData.status === 'active' && (currentGame.status === 'waiting' || currentGame.status === 'pregame'))) {
          setView('game')
        }
      }

      // Debounce the list refresh to avoid hammering the API
      // If we receive multiple game updates in a burst (e.g. on connect), we only want to fetch once.
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
      }

      refreshTimeoutRef.current = setTimeout(() => {
        listGames()
        listAvailableGames()
        refreshTimeoutRef.current = null
      }, 500) // Wait 500ms after the last update before fetching
    }

    // Add the message handler
    addMessageHandler('game_state', handleGameStateUpdate)

    // Cleanup on unmount
    return () => {
      removeMessageHandler('game_state')
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
      }
    }
  }, [isConnected, currentGame, addMessageHandler, removeMessageHandler, setCurrentGame, listGames, listAvailableGames, setView])



  // Scroll to top when view changes
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [view])

  // Timer for tournament countdowns
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 60000) // Update every minute

    return () => clearInterval(timer)
  }, [])

  // Add scroll shadow effect to header
  useEffect(() => {
    const handleScroll = () => {
      const header = document.querySelector('.dashboard-header')
      if (!header) return

      if (window.scrollY > 50) {
        header.classList.add('scrolled')
      } else {
        header.classList.remove('scrolled')
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Global navigation event listener
  useEffect(() => {
    const handleNavigateToNotifications = () => {
      setView('notifications')
    }

    const handleNavigateToGame = async (gameIdOrEvent) => {
      const gameId = typeof gameIdOrEvent === 'string' ? gameIdOrEvent : gameIdOrEvent.detail.gameId

      try {
        // Fetch the game data and set it as current
        const gameData = await getGame(gameId)
        setCurrentGame(gameData)
        setView('game')

        // Join WebSocket room for real-time updates
        if (isConnected) {
          joinWebSocketGame(gameId)
        }
      } catch (err) {
        console.error('NAVIGATION DEBUG: Error loading game:', err)
        if (window.showToast) {
          window.showToast('Failed to load game', 'error')
        }
      }
    }

    // Set up global navigation functions
    window.navigateToNotifications = handleNavigateToNotifications
    window.navigateToGame = handleNavigateToGame

    // Listen for custom navigation events
    window.addEventListener('navigate-to-notifications', handleNavigateToNotifications)
    window.addEventListener('navigate-to-game', handleNavigateToGame)

    return () => {
      delete window.navigateToNotifications
      delete window.navigateToGame
      window.removeEventListener('navigate-to-notifications', handleNavigateToNotifications)
      window.removeEventListener('navigate-to-game', handleNavigateToGame)
    }
  }, [getGame, setCurrentGame, isConnected, joinWebSocketGame])

  const ratingDisplay = isGuest ? '0' : (profileStats?.rating ?? '—')
  const gamesWonDisplay = isGuest ? 0 : (profileStats?.games_won ?? 0)
  const tournamentsWonDisplay = isGuest ? 0 : (profileStats?.tournaments_won ?? 0)
  const winRateDisplay = isGuest ? '0%' : (profileStats
    ? `${Math.round(profileStats.win_rate || 0)}%`
    : '0%')
  const displayName = profileStats?.username || 'Loading...'

  // Check if user is registered for a tournament
  const isRegisteredForTournament = (tournamentId) => {
    if (!tournaments || !user?.id) return false;

    const tournament = tournaments.find(t => t.id === tournamentId);
    if (!tournament) return false;

    // Use the is_registered field from the API response
    return tournament.is_registered || false;
  }

  // Game view is special - it needs full screen
  if (view === 'game' && currentGame) {
    return <Game
      gameId={currentGame.id}
      onBack={handleBackToDashboard}
      onViewReplay={handleViewReplay}
      onNavigateToGame={handleNavigateToGame}
      sessionWins={sessionWins}
      onUpdateSessionWins={setSessionWins}
    />
  }

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
          <div className="logo" onClick={() => { navigateToView('dashboard'); setActiveDropdown(null); }} style={{ cursor: 'pointer' }}>
            <img
              src="/images/logo/riftchesslogohorizontalfinal.png"
              alt="RiftChess"
              className="logo-horizontal"
            />
            <img
              src="/images/logo/finallogosquare.png"
              alt="RiftChess"
              className="logo-icon"
            />
          </div>
          </div>

          <div className="header-center">
          <nav className="main-nav-dropdown">
            {/* Home Link */}
            <button
              className={`nav-home-link ${view === 'dashboard' ? 'active' : ''}`}
              onClick={() => { handleViewDashboard(); setActiveDropdown(null); }}
            >
              Home
            </button>

            {/* Game Dropdown */}
            <div
              className="nav-dropdown-container"
              onMouseEnter={() => setActiveDropdown('game')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button
                className={`nav-dropdown-trigger ${(['tournaments', 'leaderboard', 'replay', 'spectate'].includes(view) || activeDropdown === 'game') ? 'active' : ''}`}
                onClick={() => setActiveDropdown(activeDropdown === 'game' ? null : 'game')}
              >
                Game
                <span className="dropdown-arrow">▼</span>
              </button>
              {activeDropdown === 'game' && (
                <div className="nav-dropdown-menu">
                  <div className="dropdown-section">
                    <div className="dropdown-section-title">Compete</div>
                    <div className="dropdown-item" onClick={() => { handleViewTournaments(); setActiveDropdown(null); }}>
                      <div className="dropdown-item-content">
                        <span className="dropdown-item-title">Tournaments</span>
                        <span className="dropdown-item-desc">Competitive matches</span>
                      </div>
                    </div>
                    <div className="dropdown-item" onClick={() => { handleViewLeaderboard(); setActiveDropdown(null); }}>
                      <div className="dropdown-item-content">
                        <span className="dropdown-item-title">Leaderboard</span>
                        <span className="dropdown-item-desc">Rankings and stats</span>
                      </div>
                    </div>
                    <div className="dropdown-item" onClick={() => { setView('replay'); setActiveDropdown(null); }}>
                      <div className="dropdown-item-content">
                        <span className="dropdown-item-title">Replay</span>
                        <span className="dropdown-item-desc">Watch and analyze games</span>
                      </div>
                    </div>
                    <div className="dropdown-item" onClick={() => { handleViewSpectate(); setActiveDropdown(null); }}>
                      <div className="dropdown-item-content">
                        <span className="dropdown-item-title">Spectate</span>
                        <span className="dropdown-item-desc">Watch live games</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Social Dropdown */}
            <div
              className="nav-dropdown-container"
              onMouseEnter={() => setActiveDropdown('social')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button
                className={`nav-dropdown-trigger ${(['friends', 'teams', 'quests'].includes(view) || activeDropdown === 'social') ? 'active' : ''}`}
                onClick={() => setActiveDropdown(activeDropdown === 'social' ? null : 'social')}
              >
                Social
                <span className="dropdown-arrow">▼</span>
              </button>
              {activeDropdown === 'social' && (
                <div className="nav-dropdown-menu">
                  <div className="dropdown-section">
                    <div className="dropdown-section-title">Community</div>
                    <div className="dropdown-item" onClick={() => { handleViewFriends(); setActiveDropdown(null); }}>
                      <div className="dropdown-item-content">
                        <span className="dropdown-item-title">Friends</span>
                        <span className="dropdown-item-desc">Connect with players</span>
                      </div>
                    </div>
                    <div className="dropdown-item" onClick={() => { setView('teams'); setActiveDropdown(null); }}>
                      <div className="dropdown-item-content">
                        <span className="dropdown-item-title">Teams</span>
                        <span className="dropdown-item-desc">Join or create teams</span>
                      </div>
                    </div>
                    <div className="dropdown-item" onClick={() => { setView('quests'); setActiveDropdown(null); }}>
                      <div className="dropdown-item-content">
                        <span className="dropdown-item-title">Quests</span>
                        <span className="dropdown-item-desc">Complete challenges</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Learn Dropdown */}
            <div
              className="nav-dropdown-container"
              onMouseEnter={() => setActiveDropdown('learn')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button
                className={`nav-dropdown-trigger ${(['whyriftchess', 'rules'].includes(view) || activeDropdown === 'learn') ? 'active' : ''}`}
                onClick={() => setActiveDropdown(activeDropdown === 'learn' ? null : 'learn')}
              >
                Learn
                <span className="dropdown-arrow">▼</span>
              </button>
              {activeDropdown === 'learn' && (
                <div className="nav-dropdown-menu">
                  <div className="dropdown-section">
                    <div className="dropdown-section-title">Resources</div>
                    <div className="dropdown-item" onClick={() => { handleViewWhyRiftChess(); setActiveDropdown(null); }}>
                      <div className="dropdown-item-content">
                        <span className="dropdown-item-title">What is RiftChess?</span>
                        <span className="dropdown-item-desc">Introduction to the game</span>
                      </div>
                    </div>
                    <div className="dropdown-item" onClick={() => { handleViewRules(); setActiveDropdown(null); }}>
                      <div className="dropdown-item-content">
                        <span className="dropdown-item-title">Rules</span>
                        <span className="dropdown-item-desc">Game rules and mechanics</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

          </nav>
          </div>

        {/* Mobile menu toggle - moved outside header-right */}
        <button
          className="mobile-menu-toggle"
          onClick={() => setShowMobileMenu(!showMobileMenu)}
        >
          ☰
        </button>

        <div className="header-right">
          <nav className="account-nav">
            {/* Account Dropdown */}
            <div
              className="nav-dropdown-container"
              onMouseEnter={() => setActiveDropdown('account')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button
                className={`nav-dropdown-trigger ${(['profile', 'gamePreferences', 'friends', 'teams'].includes(view) || activeDropdown === 'account') ? 'active' : ''}`}
                onClick={() => setActiveDropdown(activeDropdown === 'account' ? null : 'account')}
              >
                <div className="account-trigger-content">
                  <div className="profile-avatar-small">
                    <span>{displayName?.charAt(0).toUpperCase()}</span>
                    {unreadNotificationCount > 0 && (
                      <div className="notification-indicator">
                        {unreadNotificationCount > 9 ? '9+' : unreadNotificationCount}
                      </div>
                    )}
                  </div>
                  <div className="account-text">
                    <div className="account-username">
                      <span>{displayName}</span>
                    </div>
                  </div>
                </div>
                <span className="dropdown-arrow">▼</span>
              </button>
              {activeDropdown === 'account' && (
                <div className="nav-dropdown-menu account-dropdown-menu">
                  <div className="dropdown-section">
                    <div className="dropdown-section-title">Settings</div>
                    <div className="dropdown-item" onClick={() => {
                      const username = profileStats?.username || user?.user_metadata?.username || user?.email?.split('@')[0];
                      if (username) window.open(`/user/${username}`, '_blank');
                      setActiveDropdown(null);
                    }}>
                      <div className="dropdown-item-content">
                        <span className="dropdown-item-title">Public Profile ↗</span>
                        <span className="dropdown-item-desc">View your public profile</span>
                      </div>
                    </div>
                    <div className="dropdown-item" onClick={() => { handleViewProfile(); setActiveDropdown(null); }}>
                      <div className="dropdown-item-content">
                        <span className="dropdown-item-title">Profile Settings</span>
                        <span className="dropdown-item-desc">Account and preferences</span>
                      </div>
                    </div>
                    <div className="dropdown-item" onClick={() => { setView('gamePreferences'); setActiveDropdown(null); }}>
                      <div className="dropdown-item-content">
                        <span className="dropdown-item-title">Game Preferences</span>
                        <span className="dropdown-item-desc">Display and controls</span>
                      </div>
                    </div>
                  </div>
                  <div className="dropdown-section">
                    <div className="dropdown-section-title">Notifications</div>
                    <div className="dropdown-item" onClick={() => { handleViewNotifications(); setActiveDropdown(null); }}>
                      <div className="dropdown-item-content">
                        <span className="dropdown-item-title">Notifications</span>
                        <span className="dropdown-item-desc">Updates and alerts</span>
                      </div>
                    </div>
                  </div>
                  {profileStats?.account_type !== 'premium' && (
                  <div className="dropdown-section">
                    <div className="dropdown-section-title">Premium</div>
                    <div className="dropdown-item" onClick={() => {
                      setView('premium');
                      window.history.pushState({}, '', '/premium');
                      setActiveDropdown(null);
                    }}>
                      <div className="dropdown-item-content">
                        <span className="dropdown-item-title">Upgrade to Premium ★</span>
                        <span className="dropdown-item-desc">Unlock exclusive features</span>
                      </div>
                    </div>
                  </div>
                  )}
                  {isAdmin && (
                    <div className="dropdown-section">
                      <div className="dropdown-section-title">Admin</div>
                      <div className="dropdown-item" onClick={() => { handleViewAdmin(); setActiveDropdown(null); }}>
                        <div className="dropdown-item-content">
                          <span className="dropdown-item-title">🎭 Bot Admin</span>
                          <span className="dropdown-item-desc">Manage bot system</span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="dropdown-divider"></div>
                  <div className="dropdown-item sign-out-item" onClick={() => { handleSignOut(); setActiveDropdown(null); }}>
                    <div className="dropdown-item-content">
                      <span className="dropdown-item-title">Sign Out</span>
                      <span className="dropdown-item-desc">Log out of your account</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </nav>

          <div className="social-separator" aria-hidden="true"></div>

          {/* Social Media Icons */}
          <div className="social-icons">
            <a href="https://x.com/riftchess" target="_blank" rel="noopener noreferrer" className="social-icon x-twitter" aria-label="RiftChess on X">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932L18.901 1.153ZM17.61 20.644h2.039L6.486 3.24H4.298L17.61 20.644Z"/>
              </svg>
            </a>
            <a href="https://discord.gg/riftchess" target="_blank" rel="noopener noreferrer" className="social-icon discord" aria-label="RiftChess on Discord">
              <svg width="20" height="20" viewBox="0 0 71 55" fill="currentColor">
                <g clipPath="url(#clip0)">
                  <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1066 30.1693C30.1066 34.1136 27.28 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7636 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.6986 30.1693C53.6986 34.1136 50.9 37.3253 47.3178 37.3253Z"/>
                </g>
                <defs>
                  <clipPath id="clip0">
                    <rect width="71" height="55" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
            </a>
            <a href="https://instagram.com/riftchess" target="_blank" rel="noopener noreferrer" className="social-icon instagram" aria-label="RiftChess on Instagram">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
          </div>
        </div>
        </div>

        {/* Mobile Navigation */}
        <nav className={`main-nav ${showMobileMenu ? 'mobile-open' : ''}`}>
          <button className="nav-item" onClick={() => { navigateToView('dashboard'); setShowMobileMenu(false); }}>
            Home
          </button>
          <button className="nav-item" onClick={() => { handleViewTournaments(); setShowMobileMenu(false); }}>
            Tournaments
          </button>
          <button className="nav-item" onClick={() => { handleViewLeaderboard(); setShowMobileMenu(false); }}>
            Leaderboard
          </button>
          <button className="nav-item" onClick={() => { handleViewWhyRiftChess(); setShowMobileMenu(false); }}>
            Why RiftChess?
          </button>
          <button className="nav-item" onClick={() => { handleViewRules(); setShowMobileMenu(false); }}>
            Rules & Guide
          </button>
          <button className="nav-item" onClick={() => { setView('replay'); setShowMobileMenu(false); }}>
            Replay
          </button>
          <button className="nav-item" onClick={() => { handleViewSpectate(); setShowMobileMenu(false); }}>
            Spectate Games
          </button>
          <button className="nav-item" onClick={() => { handleViewFriends(); setShowMobileMenu(false); }}>
            Friends
          </button>
          <button className="nav-item" onClick={() => { handleViewNotifications(); setShowMobileMenu(false); }}>
            Notifications
          </button>
          <button className="nav-item" onClick={() => { setView('teams'); setShowMobileMenu(false); }}>
            Teams
          </button>
          <button className="nav-item" onClick={() => { setView('quests'); setShowMobileMenu(false); }}>
            Quests
          </button>
          <button className="nav-item" onClick={() => { handleViewProfile(); setShowMobileMenu(false); }}>
            Profile
          </button>
          <button className="nav-item" onClick={() => { setView('gamePreferences'); setShowMobileMenu(false); }}>
            Game Preferences
          </button>
          <button className="nav-item" onClick={() => { handleSignOut(); setShowMobileMenu(false); }}>
            Sign Out
          </button>
        </nav>
      </header>

      {error && (
        <div className="error-banner">
          Error: {error}
        </div>
      )}

      {/* Render different views based on current view state */}
      <div className="content-container">
        {view === 'profile' && (
          <Profile onBack={handleViewDashboard} onViewReplay={handleViewReplay} />
        )}

        {view === 'leaderboard' && (
          <Leaderboard onViewProfile={handleViewPublicProfile} />
        )}
        {view === 'publicProfile' && (
          <PublicProfile
            username={publicProfileUsername}
            onBack={handleBackFromPublicProfile}
            onViewReplay={handleViewReplay}
          />
        )}

        {view === 'rules' && (
          <Rules />
        )}
        {view === 'fairplay' && (
          <FairPlay />
        )}
        {view === 'replay' && (
          <ReplayImporter onBack={() => setView('dashboard')} />
        )}
        {view === 'gameReplay' && replayGameId && (
          <Replay
            gameId={replayGameId}
            moveNumber={replayMoveNumber}
            onBack={handleBackFromReplay}
          />
        )}

        {view === 'gamePreferences' && (
          <GamePreferences />
        )}

        {view === 'friends' && (
          <Friends onBack={handleViewDashboard} />
        )}
        {view === 'spectate' && (
          <SpectateGamesList />
        )}

        {view === 'notifications' && (
          <Notifications onBack={handleViewDashboard} onNotificationUpdate={fetchUnreadNotificationCount} />
        )}

        {view === 'admin' && (
          <AdminPage />
        )}

        {view === 'teams' && (
          <ComingSoon feature="Teams" onBack={handleViewDashboard} />
        )}

        {view === 'quests' && (
          <ComingSoon feature="Quests" onBack={handleViewDashboard} />
        )}

        {view === 'whyriftchess' && (
          <WhyRiftChess
            onNavigateToDashboard={() => setView('dashboard')}
            onNavigateToRules={() => setView('rules')}
          />
        )}

        {view === 'premium' && (
          <Premium onBack={handleViewDashboard} />
        )}

        {view === 'tournaments' && (
          <Tournaments
            onShowTournamentDetails={handleShowTournamentDetails}
            initialTab={selectedTournamentTab}
            onNavigateToAuth={handleNavigateToAuth}
          />
        )}
        {view === 'tournamentDetails' && selectedTournamentId && (
          <TournamentDetails
            tournamentId={selectedTournamentId}
            fromTab={selectedTournamentTab}
            onBack={handleBackFromTournamentDetails}
            onNavigateToGame={handleNavigateToGame}
            onNavigateToAuth={handleNavigateToAuth}
            onViewProfile={handleViewPublicProfile}
          />
        )}


        {view === 'dashboard' && (
        <main className="dashboard-content">
        <div className="hero-section">
          <h2>
            {profileStats ? (
              <div
                onClick={() => handleViewPublicProfile(profileStats.username)}
                style={{ cursor: 'pointer' }}
              >
                <UserDisplay user={profileStats} showTitleBadges={true} showStatusBadges={true} forceWhite={true} context="hero" />
              </div>
            ) : (
              <span className="username-white">{displayName}</span>
            )}
          </h2>
          <div className="username-separator"></div>
          <div className="quick-stats">
            <div className="stats-row">
              <div className="stat-item">
                <span className="stat-number">{ratingDisplay}</span>
                <span className="stat-label">Rating</span>
              </div>
              <div className="stat-item games-won-stat">
                <span className="stat-number">{gamesWonDisplay}</span>
                <span className="stat-label">Games Won</span>
              </div>
              <div className="stat-item tournaments-won-stat">
                <span className="stat-number">{tournamentsWonDisplay}</span>
                <span className="stat-label">Tournaments Won</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{winRateDisplay}</span>
                <span className="stat-label">Win Rate</span>
              </div>
            </div>

          </div>
        </div>

        {/* Banner Image Section */}
        <div className="banner-section">
          <img
            src="/images/v3bannerregister.png"
            alt="RiftChess v3 Registration Banner"
            className="invitational-banner"
            onClick={() => {
              // Find the monthly/invitational tournament
              const invitationalTournament = tournaments?.find(t =>
                t.name.toLowerCase().includes('monthly') ||
                t.name.toLowerCase().includes('invitational')
              );
              if (invitationalTournament) {
                handleShowTournamentDetails(invitationalTournament.id, 'big');
              } else {
                // Fallback to tournaments page if specific tournament not found
                handleViewTournaments();
              }
            }}
          />
        </div>

        <div className="dashboard-layout">
          <div className="main-content">
            <section className="games-section">
              <div className="section-header" style={{textAlign: 'left'}}>
                <h3>Game Center</h3>
              </div>
              <div className="games-grid-new">
                <div className="game-card create-game">
                  <MatchmakingDashboard onGameFound={handleNavigateToGame} />
                </div>

                <div className="game-card create-private">
                  <h4>Private Game</h4>
                  <p>Create a private game to share with friends</p>

                  <button
                    className="action-button outline"
                    onClick={() => handleCreateGame(true)}
                    disabled={loading}
                    style={{borderColor: 'var(--accent-secondary)', color: 'var(--accent-secondary)', backgroundColor: 'black'}}
                    onMouseEnter={(e) => {e.target.style.backgroundColor = 'var(--accent-secondary)'; e.target.style.color = 'black'}}
                    onMouseLeave={(e) => {e.target.style.backgroundColor = 'black'; e.target.style.color = 'var(--accent-secondary)'}}
                  >
                    {loading ? 'Creating...' : 'Create Private Game'}
                  </button>

                  <div style={{borderTop: '1px solid #333', margin: '1rem 0'}}></div>

                  <div className="games-tabbed">
                  <div className="games-header">
                    <h3>My Games</h3>
                  </div>

                  <div className="games-content" style={{minHeight: '120px', maxHeight: '120px', overflow: 'auto'}}>
                    <div className="game-list">
                    {(() => {
                      const now = new Date()
                      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)

                      // Get all user's games including private waiting games
                      const myActiveGames = (games || []).filter(game => {
                        const isRelevantStatus = game.status === 'active' || game.status === 'pregame' || game.status === 'waiting'
                        const isRecent = new Date(game.updated_at || game.created_at) > sevenDaysAgo
                        return isRelevantStatus && isRecent
                      })

                      // Sort by most recent first, limit to 2
                      const myGames = myActiveGames
                        .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
                        .slice(0, 2)

                      if (myGames.length === 0) {
                        return <p className="no-items">No games</p>
                      }

                      const copyGameLink = (gameId) => {
                        const gameUrl = `https://www.riftchess.com/game/${gameId}`
                        navigator.clipboard.writeText(gameUrl).then(() => {
                          // Could add toast notification here
                          console.log('Game link copied to clipboard')
                        }).catch(() => {
                          console.error('Failed to copy game link')
                        })
                      }

                      return myGames.map(game => (
                        <div key={game.id} className="game-item-mini">
                          {game.status === 'waiting' ? (
                            <>
                              <div className="game-info-row">
                                <div>Waiting for opponent</div>
                                {game.private && (
                                  <div className="game-private-badge">PRIVATE</div>
                                )}
                                <div className={`game-rating-badge ${game.rated && !game.tournament_id ? 'ranked' : 'non-ranked'}`}>
                                  {game.rated && !game.tournament_id ? 'Ranked' : 'Non-Ranked'}
                                </div>
                              </div>
                              <div className="game-actions-row">
                                <button
                                  className="resume-btn"
                                  onClick={() => handleResumeGame(game.id)}
                                  disabled={loading}
                                  title="Resume game"
                                >
                                  Resume
                                </button>
                                {game.private && (
                                  <button
                                    className="copy-btn"
                                    onClick={() => copyGameLink(game.id)}
                                    disabled={loading}
                                    title="Copy game link"
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                                    </svg>
                                  </button>
                                )}
                                <button
                                  className="abandon-btn"
                                  onClick={() => handleAbandonGame(game.id)}
                                  disabled={loading}
                                  title="Close game"
                                >
                                  ×
                                </button>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="game-info-row">
                                <div>
                                  vs {game.white_player_id === user?.id
                                    ? <UserDisplay user={game.black_player} showTitleBadges={false} showStatusBadges={true} />
                                    : <UserDisplay user={game.white_player} showTitleBadges={false} showStatusBadges={true} />}
                                </div>
                                {game.private && (
                                  <div className="game-private-badge">PRIVATE</div>
                                )}
                                <div className={`game-rating-badge ${game.rated && !game.tournament_id ? 'ranked' : 'non-ranked'}`}>
                                  {game.rated && !game.tournament_id ? 'Ranked' : 'Non-Ranked'}
                                </div>
                              </div>
                              <div className="game-actions-row">
                                <button
                                  className="resume-btn"
                                  onClick={() => handleResumeGame(game.id)}
                                >
                                  Resume
                                </button>
                                {game.private && (
                                  <button
                                    className="copy-btn"
                                    onClick={() => copyGameLink(game.id)}
                                    title="Copy game link"
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      ))
                        })()}
                    </div>
                  </div>
                  </div>
                </div>

              </div>
            </section>

            {/* Stats and Live Games Row */}
            <section className="stats-live-section">
              <div className="stats-live-container">
                <div className="stats-column">
                  <PlatformStats />
                </div>
                <LiveGamesPreview key={view} onViewSpectate={handleViewSpectate} />
                {profileStats?.account_type !== 'premium' && (
                <div className="premium-upgrade-column">
                  <div className="game-card premium-upgrade">
                    <div className="premium-header">
                      <h4>Upgrade to Premium <span className="premium-star-inline">★</span></h4>
                    </div>
                    <div className="premium-benefits">
                      <ul>
                        <li>FREE entry to all tournaments<br/><small style={{color: '#666'}}>(Monthly, Weekly and Daily tournaments)</small></li>
                        <li>Unlimited game history</li>
                        <li>Custom themes</li>
                        <li>Enhanced player profiles</li>
                      </ul>
                    </div>
                    <div className="free-trial-banner" style={{color: 'var(--accent-green)', fontWeight: 'bold', fontSize: '0.9rem', marginBottom: '4px', textAlign: 'center'}}>
                      30 days FREE!
                    </div>
                    <div className="cancel-anytime" style={{color: '#666', fontSize: '0.75rem', textAlign: 'center', marginBottom: '8px'}}>
                      (Cancel anytime)
                    </div>
                    <div className="premium-pricing">
                      <div className="pricing-option">
                        <div className="price-label">Monthly</div>
                        <div className="price-value">$7 / month</div>
                      </div>
                      <div className="pricing-option">
                        <div className="price-label">Yearly</div>
                        <div className="price-value">$70 / year</div>
                      </div>
                    </div>
                    <button
                      className="upgrade-button"
                      onClick={() => {
                        setView('premium')
                        window.history.pushState({}, '', '/premium')
                      }}
                    >
                      Upgrade Now
                    </button>
                  </div>
                </div>
                )}
              </div>
            </section>

            <section className="tournaments-section">
              <div className="section-header" style={{textAlign: 'left'}}>
                <h3>Tournaments</h3>
              </div>
              <div className="tournament-grid">
                {tournaments && tournaments.length > 0 ? (
                  (() => {
                    // Find the biggest tournament (highest max_players or no limit)
                    // Find the biggest tournament (highest max_players or no limit)
                    const featuredTournament = tournaments.find(t => 
                      t.name.toLowerCase().includes('monthly')
                    ) || tournaments.find(t => 
                      t.name.toLowerCase().includes('weekly')
                    ) || tournaments.find(t => 
                      t.name.toLowerCase().includes('daily')
                    ) || tournaments.find(t => 
                      (!t.max_players || t.max_players > 32)
                    ) || tournaments[0];

                    const formatTimeControl = (timeControl) => {
                      if (!timeControl) return 'Classic'
                      const { initial_time, increment } = timeControl
                      if (!initial_time && !increment) return 'Classic'
                      const minutes = Math.floor(initial_time / 60)
                      return `${minutes}+${increment}`
                    }

                    const getBigTournamentMinimum = (tournament) => {
                      const name = tournament.name.toLowerCase()
                      if (name.includes('monthly')) return 1000
                      if (name.includes('weekly')) return 100
                      if (name.includes('daily')) return 50
                      return 100
                    }

                    const getScheduledStartTime = (tournament) => {
                      const name = tournament.name.toLowerCase();
                      const now = new Date(); // Use local consistency
                      
                      if (name.includes('monthly')) {
                        // January 31st, 2026 at 18:00 CET
                        return new Date('2026-01-31T18:00:00+01:00');
                      } else if (name.includes('weekly')) {
                        // Next Saturday at 18:00 CET
                        const nextSaturday = new Date(now);
                        nextSaturday.setDate(now.getDate() + (6 - now.getDay() + 7) % 7);
                        nextSaturday.setHours(18, 0, 0, 0);
                        if (nextSaturday <= now) {
                          nextSaturday.setDate(nextSaturday.getDate() + 7);
                        }
                        return nextSaturday;
                      } else if (name.includes('daily')) {
                        // Every day at 20:00 CET
                        const nextDaily = new Date(now);
                        nextDaily.setHours(20, 0, 0, 0);
                         if (nextDaily <= now) {
                          nextDaily.setDate(nextDaily.getDate() + 1);
                        }
                        return nextDaily;
                      }
                      
                      return tournament.start_time ? new Date(tournament.start_time) : null
                    }

                    const isBigTournament = !featuredTournament.max_players || featuredTournament.max_players > 32
                    const startTime = isBigTournament ? getScheduledStartTime(featuredTournament) : (featuredTournament.start_time ? new Date(featuredTournament.start_time) : null)
                    const prizePool = featuredTournament.prize_pool?.total_amount ?
          `$${featuredTournament.prize_pool.total_amount}` : "TBD"
                    const isRegistered = isRegisteredForTournament(featuredTournament.id)

                    const formatCountdown = (startTime) => {
                      if (!startTime) return '';
                      const now = currentTime;
                      const start = new Date(startTime);
                      const diff = start - now;
                      if (diff <= 0) return 'Starting soon';
                      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
                      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                      if (days > 0) return `${days}d ${hours}h`;
                      if (hours > 0) return `${hours}h ${minutes}m`;
                      return `${minutes}m`;
                    }

                    return (
                      <div className="tournament-card featured">
                        <div className="tournament-badge">FEATURED</div>
                        <div className="tournament-header" style={{display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start'}}>
                          <h4>{featuredTournament.name.replace(/\\s*\\(bracket\\)|\\s*\\(round_robin\\)|\\s*Sit & Go/gi, '')}</h4>
                          {isRegistered && (
                            <div style={{
                              color: '#22c55e',
                              fontSize: '0.8rem',
                              fontWeight: '600',
                              marginTop: '4px'
                            }}>
                              REGISTERED
                            </div>
                          )}
                        </div>
                        <div style={{marginBottom: '1rem'}}>
                          <p>Join our {featuredTournament.name.toLowerCase().includes('monthly') ? 'biggest tournament of the month' : 'featured tournament'} with guaranteed prize pool</p>
                        </div>
                        <div style={{display: 'flex', gap: '2rem'}}>
                          <div style={{flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
                            <div style={{fontSize: '1rem', color: '#444'}}>Entry: <span style={{color: '#ffffff'}}>Free</span></div>
                            <div style={{fontSize: '1rem', color: '#444'}}>Registered Players: <span style={{color: '#ffffff'}}>{featuredTournament.players_count}{featuredTournament.max_players ? `/${featuredTournament.max_players}` : ''}</span></div>
                            {startTime && (
                              <div style={{fontSize: '1rem', color: '#444'}}>Starts in: <span style={{color: '#ffffff'}}>{formatCountdown(startTime)}</span></div>
                            )}
                          </div>
                          <div style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                            <div style={{fontSize: '3rem', fontWeight: '700', color: 'var(--accent-secondary)'}}>{prizePool}</div>
                          </div>
                        </div>
                        <div style={{borderTop: '1px solid #333', margin: '1.5rem 0 1rem 0'}}></div>
                        {/* Desktop layout */}
                        <div className="tournament-actions tournament-actions-desktop" style={{display: 'flex', gap: '0.5rem'}}>
                          <button
                            className={`action-button ${isRegistered ? 'outline' : 'primary'}`}
                            style={{width: '70%', ...(isRegistered ? {borderColor: '#f87171', color: '#f87171'} : {backgroundColor: '#84cc16', borderColor: '#84cc16'})}}
                            onClick={() => isRegistered ? handleTournamentLeave(featuredTournament.id) : handleTournamentJoin(featuredTournament.id)}
                            onMouseEnter={isRegistered ? (e) => {e.target.style.backgroundColor = '#f87171'; e.target.style.borderColor = '#f87171'; e.target.style.color = '#ffffff'} : undefined}
                            onMouseLeave={isRegistered ? (e) => {e.target.style.backgroundColor = 'transparent'; e.target.style.borderColor = '#f87171'; e.target.style.color = '#f87171'} : undefined}
                            disabled={loading}
                          >
                            {loading ? (
                              <span style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'}}>
                                <span style={{
                                  width: '16px',
                                  height: '16px',
                                  border: '2px solid transparent',
                                  borderTop: '2px solid currentColor',
                                  borderRadius: '50%',
                                  animation: 'spin 1s linear infinite'
                                }}></span>
                                {isRegistered ? 'Leaving...' : 'Joining...'}
                              </span>
                            ) : (
                              isRegistered ? 'Unregister' : 'Register Now'
                            )}
                          </button>
                          <button className="action-button outline" style={{borderColor: '#333', color: '#555', width: '30%'}} onClick={() => handleShowTournamentDetails(featuredTournament.id, 'big')} onMouseEnter={(e) => {e.target.style.backgroundColor = '#333'; e.target.style.borderColor = '#333'; e.target.style.color = '#ffffff'}} onMouseLeave={(e) => {e.target.style.backgroundColor = 'transparent'; e.target.style.borderColor = '#333'; e.target.style.color = '#555'}}>Details</button>
                        </div>
                        {/* Mobile layout */}
                        <div className="tournament-mobile-actions">
                          <button
                            className={`action-button ${isRegistered ? 'outline' : 'primary'}`}
                            style={isRegistered ? {borderColor: '#f87171', color: '#f87171'} : {backgroundColor: '#84cc16', borderColor: '#84cc16'}}
                            onClick={() => isRegistered ? handleTournamentLeave(featuredTournament.id) : handleTournamentJoin(featuredTournament.id)}
                            onMouseEnter={isRegistered ? (e) => {e.target.style.backgroundColor = '#f87171'; e.target.style.borderColor = '#f87171'; e.target.style.color = '#ffffff'} : undefined}
                            onMouseLeave={isRegistered ? (e) => {e.target.style.backgroundColor = 'transparent'; e.target.style.borderColor = '#f87171'; e.target.style.color = '#f87171'} : undefined}
                            disabled={loading}
                          >
                            {loading ? (
                              <span style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'}}>
                                <span style={{
                                  width: '16px',
                                  height: '16px',
                                  border: '2px solid transparent',
                                  borderTop: '2px solid currentColor',
                                  borderRadius: '50%',
                                  animation: 'spin 1s linear infinite'
                                }}></span>
                                {isRegistered ? 'Leaving...' : 'Joining...'}
                              </span>
                            ) : (
                              isRegistered ? 'Unregister' : 'Register'
                            )}
                          </button>
                          <button className="action-button outline" style={{borderColor: '#333', color: '#555'}} onClick={() => handleShowTournamentDetails(featuredTournament.id, 'big')} onMouseEnter={(e) => {e.target.style.backgroundColor = '#333'; e.target.style.borderColor = '#333'; e.target.style.color = '#ffffff'}} onMouseLeave={(e) => {e.target.style.backgroundColor = 'transparent'; e.target.style.borderColor = '#333'; e.target.style.color = '#555'}}>Details</button>
                        </div>
                      </div>
                    )
                  })()
                ) : (
                  <div className="tournament-card featured">
                    <div className="tournament-badge">FEATURED</div>
                    <div className="tournament-header">
                      <h4>Monthly Championship</h4>
                    </div>
                    <div style={{marginBottom: '1rem'}}>
                      <p>Join our biggest tournament of the month with guaranteed prize pool</p>
                    </div>
                    <div style={{display: 'flex', gap: '2rem'}}>
                      <div style={{flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
                        <div style={{fontSize: '1rem', color: '#444'}}>Entry: <span style={{color: '#ffffff'}}>Free</span></div>
                        <div style={{fontSize: '1rem', color: '#444'}}>Registered Players: <span style={{color: '#ffffff'}}>0</span></div>
                        <div style={{fontSize: '1rem', color: '#444'}}>Starts in: <span style={{color: '#ffffff'}}>
                          {(() => {
                            const startTime = new Date();
                            startTime.setDate(startTime.getDate() + 7); // Placeholder: next week
                            const now = currentTime;
                            const diff = startTime - now;
                            if (diff <= 0) return 'Starting soon';
                            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
                            const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                            if (days > 0) return `${days}d ${hours}h`;
                            return `${hours}h`;
                          })()}
                        </span></div>
                      </div>
                      <div style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <div style={{fontSize: '3rem', fontWeight: '700', color: 'var(--accent-secondary)'}}>$1000</div>
                      </div>
                    </div>
                    <div style={{borderTop: '1px solid #333', margin: '1.5rem 0 1rem 0'}}></div>
                    <div className="tournament-actions" style={{display: 'flex', gap: '0.5rem'}}>
                      <button className="action-button primary" style={{width: '70%', backgroundColor: '#84cc16', borderColor: '#84cc16'}} onClick={() => isGuest ? setShowQuestModal(true) : setView('tournaments')}>Register Now</button>
                      <button className="action-button outline" style={{borderColor: '#333', color: '#555', width: '30%'}} onClick={() => setView('tournaments')} onMouseEnter={(e) => {e.target.style.backgroundColor = '#333'; e.target.style.borderColor = '#333'; e.target.style.color = '#ffffff'}} onMouseLeave={(e) => {e.target.style.backgroundColor = 'transparent'; e.target.style.borderColor = '#333'; e.target.style.color = '#555'}}>Details</button>
                    </div>
                  </div>
                )}

                <div className="tournament-explore">
                  <h4>Explore More Tournaments</h4>
                  <p>Discover the full range of RiftChess tournaments and Sit & Go events available. From quick 8-player brackets to massive scheduled championships.</p>

                  <div className="explore-features">
                    <div className="feature-row">
                      <strong>Sit & Go Tournaments</strong>
                      <span>Start every few minutes when full</span>
                    </div>
                    <div className="feature-row">
                      <strong>Scheduled Championships</strong>
                      <span>Daily, weekly, and monthly events</span>
                    </div>
                    <div className="feature-row">
                      <strong>Multiple Formats</strong>
                      <span>Bracket elimination and round-robin</span>
                    </div>
                    <div className="feature-row">
                      <strong>Real Prizes</strong>
                      <span>Compete for guaranteed prize pools</span>
                    </div>
                  </div>

                  <button
                    className="action-button outline"
                    style={{borderColor: '#333', color: '#555'}}
                    onMouseEnter={(e) => {e.target.style.backgroundColor = '#333'; e.target.style.borderColor = '#333'; e.target.style.color = '#ffffff'}}
                    onMouseLeave={(e) => {e.target.style.backgroundColor = 'transparent'; e.target.style.borderColor = '#333'; e.target.style.color = '#555'}}
                    onClick={() => setView('tournaments')}
                  >
                    View All Tournaments
                  </button>
                </div>
              </div>
            </section>
          </div>

        </div>
        </main>
        )}
      </div>

      <ConfirmModal
        isOpen={showAbandonModal}
        onClose={() => setShowAbandonModal(false)}
        onConfirm={confirmAbandonGame}
        title="Abandon Game"
        message="Are you sure you want to abandon this game? This action cannot be undone."
        confirmText="Abandon"
        cancelText="Cancel"
        type="danger"
      />

      <QuestAccountModal
        isOpen={showQuestModal}
        onClose={() => setShowQuestModal(false)}
        onCreateAccount={handleCreateAccount}
      />

      {/* Private Game Modal */}
      {showPrivateGameModal && (
        <div className="modal-overlay" onClick={() => setShowPrivateGameModal(false)}>
          <div className="modal-content private-game-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Private Game Created!</h3>
              <button className="modal-close" onClick={() => setShowPrivateGameModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Share this link with your friend to invite them:</p>
              <div className="game-url-container">
                <input
                  type="text"
                  value={privateGameUrl}
                  readOnly
                  className="game-url-input"
                />
                <button
                  className="copy-button"
                  onClick={copyPrivateGameUrl}
                  title="Copy to clipboard"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                  </svg>
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="action-button primary"
                onClick={handleJoinPrivateGame}
              >
                Join Game
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
